import styled from '@emotion/styled'
import { AnimatePresence, motion } from 'framer-motion'
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { BsXLg } from 'react-icons/bs'

import useAnimationState from '@/hooks/useAnimationState'

interface IProps {
  /*
   * 是否显示
   */
  show: boolean
  /*
   * 是否点击遮罩关闭
   */
  shadowClick?: boolean
  /*
   * 标题
   */
  title?: string
  /*
   * 头部
   */
  header?: React.ReactNode
  /*
   * 底部
   */
  footer?: React.ReactNode
  /*
   * 取消文案
   */
  cancelText?: string
  /*
   * 确认按钮启用状态
   */
  confirmDisable?: boolean
  /*
   * 确认文案
   */
  confirmText?: string
  /*
   * 取消事件
   */
  cancel?: ((status: boolean) => void) | null
  /*
   * 确认事件
   */
  confirm?: (() => void) | null
  /*
   * 内容体
   */
  children: React.ReactNode
  /*
   * 自定义弹窗体样式
   */
  style?: CSSProperties | undefined
  /*
   * 自定义弹窗体样式名
   */
  className?: any
  /*
   * 模式 0 弹出 1 划出 默认 0
   */
  mode?: number
  /*
   * 右上角关闭
   */
  close?: boolean
  /*
   * 是否有遮罩，默认true
   */
  mask?: boolean
}

const defAnimate = {
  initial: { opacity: 0, scale: 0.3 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.3 },
  transition: {
    duration: 0.25,
    type: 'spring',
    damping: 35,
    stiffness: 600
  }
}

export const fullAnimate = {
  initial: { y: '110%' },
  animate: { y: 20 },
  exit: { y: '110%' },
  transition: {
    duration: 0.25,
    type: 'spring',
    damping: 40,
    stiffness: 600
  }
}

const maskAnimate = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25 }
}

export default function Index({
  show = false,
  shadowClick = true,
  header = null,
  footer = null,
  cancelText = '取消',
  confirmDisable = false,
  confirmText = '确定',
  title = '',
  cancel = null,
  confirm = null,
  children,
  style = {},
  className = '',
  mode = 0,
  close = true,
  mask = true
}: IProps) {
  const [localState, animation, setAnimationState] = useAnimationState()
  const [bodyOverflow, setBodyOverflow] = useState('')
  const [tid] = useState(new Date().getTime())
  const ref = useRef<HTMLElement>()
  const spriteRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      return
    }
    ref.current = document.createElement('div')
    ref.current.classList.add('modal')
    document.body.appendChild(ref.current)
    return () => {
      // 页面销毁时，移除外层元素
      document.body.removeChild(ref.current as Node)
    }
  }, [])

  useEffect(() => {
    setAnimationState(show)
    if (show) {
      if (document.body.style.overflow !== 'hidden') {
        setBodyOverflow(document.body.style.overflow)
        document.body.style.overflow = 'hidden'
      }
    } else {
      setTimeout(() => {
        const modal_container: any = document.querySelector('.modal_container')
        if (!modal_container) {
          document.body.style.overflow = bodyOverflow
        }
      }, 500)
    }
  }, [show, bodyOverflow])

  const _cancel = () => {
    cancel && cancel(false)
  }

  const handleContainerClick = (e: React.MouseEvent) => {
    console.log('handleContainerClick')
    if (!shadowClick || mask) return

    const spriteElement = spriteRef.current
    if (spriteElement && !spriteElement.contains(e.target as Node)) {
      _cancel()
    }
  }

  return ref.current
    ? ReactDOM.createPortal(
        localState ? (
          <Container className={`modal_container w-screen h-screen fixed top-0 left-0 flex ${mode ? 'items-end' : 'items-center'} justify-center z-[9000]`} onClick={handleContainerClick}>
            <AnimatePresence>
              {animation && mask ? (
                <motion.div
                  {...maskAnimate}
                  key="mask"
                  className="mark bg-[#00000050] w-full h-full absolute top-0 left-0 z-[9001]"
                  onClick={() => {
                    shadowClick && _cancel()
                  }}
                ></motion.div>
              ) : null}
              {animation ? (
                <motion.div {...(mode ? fullAnimate : defAnimate)} className="sprite-box max-h-screen overflow-auto z-[9002] py-[10px] px-[10px]">
                  <div key="sprite" ref={spriteRef} className={`${className} sprite`} style={style}>
                    {header !== null ? (
                      header
                    ) : (
                      <div className="header flex items-center justify-center pb-[10px] mb-[10px] relative">
                        <div className=" text-[20px] text-[#333] flex-1">{title}</div>
                        {close ? (
                          <div onClick={_cancel} className="w-[30px] h-[30px] flex items-center justify-center cursor-pointer text-[#666] anim_btn2">
                            <BsXLg />
                          </div>
                        ) : null}
                      </div>
                    )}
                    {localState ? children : null}
                    {footer !== null ? (
                      footer
                    ) : (
                      <div className=" flex items-center justify-center mt-[10px]">
                        {cancelText !== '' ? (
                          <button onClick={_cancel} className="mx-[10px] rounded-full px-[40px] py-[9px] text-[#ff3300] bg-[#ffeee8] border-[#ff3300] border-[1px] border-solid anim_btn2">
                            {cancelText}
                          </button>
                        ) : null}
                        {confirm !== null ? (
                          <button
                            disabled={confirmDisable}
                            onClick={confirm}
                            className={`mx-[10px] rounded-full px-[40px] py-[10px] text-[#FFF] ${confirmDisable ? 'btn-disabled' : 'btn-style anim_btn2'}`}
                          >
                            {confirmText}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </Container>
        ) : null,
        ref.current
      )
    : null
}

const Container = styled(motion.div)`
  .sprite-box {
    overflow: inherit;
    ::-webkit-scrollbar {
      width: 0;
      height: 0;
      background-color: transparent;
    }
    ::-webkit-scrollbar-track {
      /*-webkit-box-shadow:inset 0 0 6px rgba(0,0,0,0.6);*/
      border-radius: 8px;
      /*background-color: rgba(255,255,255,0.2);*/
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 0;
      /*-webkit-box-shadow:inset 0 0 6px rgba(0,0,0,.3);*/
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
  .sprite {
    min-width: 400px;
    min-height: 150px;
    padding: 20px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0px 0px 0px 4px rgba(255, 255, 255, 0.25);
    .header {
      ::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 2px;
        background: #f3f3f3;
      }
    }
    .footer {
    }
    .btn-disabled {
      background: #6c6b6b;
      cursor: no-drop;
    }
    .btn-style {
      background: linear-gradient(39deg, #ff742f 0%, #ff3300 100%);
    }
  }
`
